// Widget de mapa en Dashboard

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography } from "@mui/material";
import { MapContainer, TileLayer, Marker, Popup, useMap, Tooltip } from "react-leaflet";
import { usePage } from '../PageContext';
import { apiFetchJson } from '../fetchWrapper';
import { formatDashedDate } from '../util/DateUtil';
import {
  populateSiteData,
  getSiteDataForSite,
  getIconByEnrichedSite,
  getAttendanceIcon
} from '../util/MapUtil';
import { getAttendanceStatusLabel } from './AttendanceStatus';

function FixMapResize() {
  const map = useMap();

  useEffect(() => {
    const t = requestAnimationFrame(() => {
      map.invalidateSize();
    });

    return () => cancelAnimationFrame(t);
  }, [map]);

  return null;
}

const MapWidget = ({
  assignments,
  selectedDay,
  hideTitle,
  site,
  withCard = true,
  height = 250
}) => {
  const [sites, setSites] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const { setPage } = usePage();

  const defaultLat = 19.4326;
  const defaultLng = -99.1332;
  const defaultZoom = 12;

  useEffect(() => {
    const now = new Date();

    // antes de las 7:00, usar ayer
    if (now.getHours() < 7) {
      now.setDate(now.getDate() - 1);
    }

    const dateStr = formatDashedDate(now);

    Promise.all([
      apiFetchJson(`/api/sites`),
      apiFetchJson(`/api/shift-templates`),
      apiFetchJson(`/api/attendances?startDate=${dateStr}`),
      apiFetchJson(`/api/scheduled-shifts?startDate=${dateStr}`),
      apiFetchJson(`/api/employees`)
    ]).then(([sites, templates, attendances, shifts, employees]) => {
      setSites(Array.isArray(sites) ? sites : []);
      setShiftTemplates(Array.isArray(templates) ? templates : []);
      setAttendances(Array.isArray(attendances) ? attendances : []);
      setShifts(Array.isArray(shifts) ? shifts : []);
      setEmployees(Array.isArray(employees) ? employees : []);
    });
  }, []);


  const siteData = useMemo(() => {
    if (
      !Array.isArray(sites) ||
      !Array.isArray(shiftTemplates) ||
      !Array.isArray(shifts) ||
      !Array.isArray(employees) ||
      sites.length === 0 ||
      shiftTemplates.length === 0 ||
      shifts.length === 0 ||
      employees.length === 0
    ) return [];

    return populateSiteData(
      shiftTemplates,
      attendances,
      shifts,
      sites,
      employees
    );
  }, [sites, shiftTemplates, attendances, shifts, employees]);

  const handleCardClick = () => {
    setPage('sites');
  };

  const content = (
    <>
      {!hideTitle && (
        <Typography variant="h6" fontSize={25} fontWeight={"bold"}>
          Ubicaciones
        </Typography>
      )}

      <Box sx={{ height, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <MapContainer
          center={[defaultLat, defaultLng]}
          zoom={defaultZoom}
          style={{ height: "100%", width: "100%" }} 
        >
          <FixMapResize />

          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {sites.map((site) => {
            if (site.latitude == null || site.longitude == null) return null;

            const enrichedSite = getSiteDataForSite(site.id, siteData);
            const att = enrichedSite?.attendance;

            const siteIcon = getIconByEnrichedSite(enrichedSite);

            const attendanceIcon = att
              ? getAttendanceIcon(att)
              : null;

            return (
              <React.Fragment key={site.id}>
                <Marker
                  position={[site.latitude, site.longitude]}
                  icon={siteIcon}
                >
                  <Tooltip>
                    {site.name} - {getAttendanceStatusLabel(enrichedSite?.status)}
                  </Tooltip>

                  <Popup>
                    <strong>{site.name}</strong>
                    <br />
                    {site.address.street}, {site.address.city}, {site.address.state}
                    <br />
                    Estado: {getAttendanceStatusLabel(enrichedSite?.status)}
                  </Popup>
                </Marker>

                {att?.latitude != null && att?.longitude != null && (
                  <Marker
                    key={`attendance-${site.id}`}
                    position={[att.latitude, att.longitude]}
                    icon={attendanceIcon}
                  >
                    <Tooltip>
                      {enrichedSite?.employee
                        ? `${enrichedSite.employee.user.person.lastnameFirst} ${enrichedSite.employee.user.person.firstname} - ${site.name}`
                        : site.name}
                    </Tooltip>
                  </Marker>
                )}
              </React.Fragment>
            );
          })}
        </MapContainer>
      </Box>
    </>
  );

  if (!withCard) return content;

  return (
    <Card
      sx={{
        height: 325,
        borderRadius: 3,
        backgroundColor: '#eef0f2',
        color: 'black',
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ height: "100%" }}>
        {content}
      </CardContent>
    </Card>
  );
};

export default MapWidget;